import { createRoute, Link } from '@tanstack/react-router'
import { Alert, AlertDescription, Button, EntrySheetTitle, MutedText, Stack } from '@volee/ui'

import { sendInsidersOn } from '../auth/entrance.ts'
import { DeviceAccountsToContinue } from '../components/device-accounts-to-continue.tsx'
import { MfaForm, NewPasswordForm, SignInForm } from '../components/sign-in-form.tsx'
import { useSignIn } from '../data/use-sign-in.ts'
import { messages } from '../messages/index.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { entryRoute } from './entry.tsx'

// The login: the sheet over the entrance. The first access is a STATE of this page and not a
// route: it cannot be re-entered on its own.

// The values first, the type derived from them, as `ROLES`/`Role` does in @volee/api-client.
const SIGN_IN_REASONS = ['session-expired', 'required', 'account-created'] as const

export type SignInReason = (typeof SIGN_IN_REASONS)[number]

/** `returnTo` is optional so a plain link to the login stays the normal way to offer it:
 *  the router derives what a link must write from this very type. */
export interface SignInSearch {
  readonly returnTo?: string
  readonly reason?: SignInReason
  /** «Aggiungi un account» from the menu (#302): the account in use stays on the phone. */
  readonly mode?: 'add'
}

/** Where the login lands when the search carries no destination of its own. */
export const DEFAULT_RETURN_TO = NAV_PATHS.home

/** Accepts only a relative path within this app: without the check `returnTo` is an open
 *  redirect (apps/frontend/docs/razionale-che-il-codice-indica.md). */
export function parseReturnTo(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_RETURN_TO
  if (!value.startsWith('/') || value.startsWith('//')) return DEFAULT_RETURN_TO
  return value
}

/** `undefined` for a search that carries none, so a bare visit keeps a bare URL: writing the
 *  default back would stamp `?returnTo=%2F` on every landing. A hostile value still
 *  collapses to the default rather than passing through. */
function optionalReturnTo(value: unknown): string | undefined {
  return value === undefined ? undefined : parseReturnTo(value)
}

/** A whitelist, because the value selects a sentence shown on a Volée-branded page. */
export function parseReason(value: unknown): SignInReason | undefined {
  if (typeof value !== 'string') return undefined
  return (SIGN_IN_REASONS as readonly string[]).includes(value)
    ? (value as SignInReason)
    : undefined
}

/** The search of the login, parsed the same on every route that can carry it: the door's old
 *  links are redirected with their search as it was, so one parser has to run on both. */
export function validateSignInSearch(search: Record<string, unknown>): SignInSearch {
  return {
    returnTo: optionalReturnTo(search.returnTo),
    reason: parseReason(search.reason),
    ...(search.mode === 'add' ? { mode: 'add' as const } : {}),
  }
}

/** The login as a link target, carrying where to come back to. A constant would have to
 *  name one destination, and every caller has its own. `adding` is the login that leaves the
 *  account in use on the phone, the one an insider may reach. */
export function signInLink(returnTo?: string, options: { readonly adding?: boolean } = {}) {
  const mode = options.adding === true ? { mode: 'add' as const } : {}
  return { to: NAV_PATHS.signIn, search: { returnTo, ...mode } } as const
}

export const signInRoute = createRoute({
  getParentRoute: () => entryRoute,
  path: NAV_PATHS.signIn,
  validateSearch: validateSignInSearch,
  // Adding an account is the one sign-in a session may see: the account in use stays on the phone.
  beforeLoad: ({ context, search }) =>
    search.mode === 'add' ? undefined : sendInsidersOn(context.queryClient),
  component: SignIn,
})

// Annotated here: this is the line that stops compiling when a reason gains no sentence.
const REASON_TEXT: Record<SignInReason, string> = messages.signIn.reason

function SignIn() {
  const { returnTo = DEFAULT_RETURN_TO, reason, mode } = signInRoute.useSearch()
  const adding = mode === 'add'
  const { pending, credentials, newPassword, mfa } = useSignIn(returnTo, { adding })

  // `info`, not `destructive`: an expired session is not the user's mistake.
  function reasonNotice() {
    if (reason === undefined || pending !== null) return null
    return (
      <Alert variant="info">
        <AlertDescription>{REASON_TEXT[reason]}</AlertDescription>
      </Alert>
    )
  }

  // Always a title, whichever state: it names the dialog and takes the focus.
  function heading() {
    if (pending?.kind === 'new-password-required') {
      return { title: messages.newPassword.title, description: messages.newPassword.description }
    }
    if (pending?.kind === 'mfa-required') {
      return { title: messages.mfa.title, description: messages.mfa.description }
    }
    if (adding) return messages.signIn.add
    return { title: messages.signIn.title, description: messages.signIn.description }
  }

  function body() {
    if (pending?.kind === 'new-password-required') return <NewPasswordForm {...newPassword} />
    if (pending?.kind === 'mfa-required') return <MfaForm {...mfa} />
    return (
      <Stack gap="md">
        <SignInForm {...credentials} />
        <Button variant="ghost" asChild>
          {/* The literal, not the route object: /forgot-password links back here. */}
          <Link to={NAV_PATHS.forgotPassword} search={{ returnTo }}>
            {messages.signIn.forgotPassword}
          </Link>
        </Button>
        {adding ? (
          <Button variant="outline" asChild>
            {/* The door sends whoever is in use back to their profile. */}
            <Link to={NAV_PATHS.home}>{messages.signIn.add.back}</Link>
          </Button>
        ) : (
          <>
            <Button variant="outline" asChild>
              <Link to={NAV_PATHS.signUp}>{messages.signIn.registerClub}</Link>
            </Button>
            <DeviceAccountsToContinue />
          </>
        )}
      </Stack>
    )
  }

  const { title, description } = heading()

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <EntrySheetTitle>{title}</EntrySheetTitle>
        <MutedText>{description}</MutedText>
      </Stack>
      {reasonNotice()}
      {body()}
    </Stack>
  )
}
